import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { Alert, Button, Form } from 'react-bootstrap'
import { useLocation } from 'react-router-dom'
import useAuth from 'hooks/useAuth'
import httpClient, { removeCacheItem } from 'utility/httpClient.util'

const CONFIG_CODE = 'designer_visible_ui_option'
const CONFIG_URL = `/core/config/configCode/${CONFIG_CODE}`
const CONFIG_LIST_URL = '/core/config?systemServiceTypeCode=core&perPage=1000'

const DebugFieldVisibilityContext = createContext(null)

const parseObject = (value) => {
  if (typeof value === 'string') {
    try {
      return parseObject(JSON.parse(value))
    } catch (error) {
      return {}
    }
  }
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {}
}

const getConfig = (response) => {
  const data = response?.data?.data
  return Array.isArray(data) ? data[0] || {} : data || response?.data || {}
}

export const DebugFieldVisibilityProvider = ({
  formKey,
  legacyFormKey,
  children,
  showControls = true,
  enabled = true,
  waitForConfig = false,
}) => {
  const location = useLocation()
  const { config = [], setAccountConfig, isAdmin } = useAuth()
  const isDebugMode = isAdmin === true && ['1', 'true'].includes(
    new URLSearchParams(location.search).get('isDebug'),
  )
  const accountConfig = config.find((item) => item.configCode === CONFIG_CODE)
  const [freshConfig, setFreshConfig] = useState(null)
  const [isConfigReady, setIsConfigReady] = useState(!waitForConfig)
  const [changes, setChanges] = useState({})
  const legacyKeys = useRef({})
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const configVisibility = parseObject((freshConfig || accountConfig)?.jsonData)
  const hasLegacyVisibility = legacyFormKey && legacyFormKey !== formKey &&
    Object.prototype.hasOwnProperty.call(configVisibility, legacyFormKey)
  const needsMigration = hasLegacyVisibility &&
    !Object.prototype.hasOwnProperty.call(configVisibility, formKey)
  const savedVisibility = parseObject(
    configVisibility[formKey] || (needsMigration ? configVisibility[legacyFormKey] : null),
  )
  const hasChanges = needsMigration || Object.keys(changes).some(
    (key) => changes[key] !== ((Object.prototype.hasOwnProperty.call(savedVisibility, key)
      ? savedVisibility[key] : savedVisibility[legacyKeys.current[key]]) !== false),
  )

  useEffect(() => {
    if (!enabled) return undefined
    let active = true
    const loadConfig = () => {
      httpClient.get(CONFIG_URL, { cache: false })
        .then((response) => {
          if (!active) return
          setFreshConfig(getConfig(response))
          setIsConfigReady(true)
        })
        .catch(() => {
          if (active && waitForConfig) {
            setFreshConfig({})
            setIsConfigReady(true)
          }
        })
    }
    loadConfig()
    return () => {
      active = false
    }
  }, [enabled, waitForConfig])

  const isVisible = (key, legacyKey) =>
    (isDebugMode && Object.prototype.hasOwnProperty.call(changes, key)
      ? changes[key]
      : Object.prototype.hasOwnProperty.call(savedVisibility, key)
        ? savedVisibility[key] : savedVisibility[legacyKey]) !== false

  const setVisible = (key, visible, legacyKey) => {
    setError('')
    if (legacyKey) legacyKeys.current[key] = legacyKey
    setChanges((current) => ({ ...current, [key]: visible }))
  }

  const save = async () => {
    if (!isDebugMode || isSaving || !hasChanges) return
    setIsSaving(true)
    setError('')
    try {
      const remote = getConfig(await httpClient.get(CONFIG_URL, { cache: false }))
      const currentConfig = remote?.configCode
        ? remote
        : accountConfig || { configCode: CONFIG_CODE }
      const jsonData = parseObject(currentConfig.jsonData)
      const visibility = { ...parseObject(
        jsonData[formKey] || (needsMigration ? jsonData[legacyFormKey] : null),
      ) }
      Object.entries(changes).forEach(([key, visible]) => {
        if (legacyKeys.current[key]) delete visibility[legacyKeys.current[key]]
        if (visible) delete visibility[key]
        else visibility[key] = false
      })
      const nextConfig = {
        ...currentConfig,
        configCode: CONFIG_CODE,
        jsonData: { ...jsonData, [formKey]: visibility },
      }
      await httpClient.put(CONFIG_URL, nextConfig)
      await Promise.all([
        removeCacheItem(CONFIG_URL),
        removeCacheItem(CONFIG_LIST_URL),
      ]).catch(() => {})
      setFreshConfig(nextConfig)
      setChanges({})
      if (setAccountConfig) {
        setAccountConfig([
          ...config.filter((item) => item.configCode !== CONFIG_CODE),
          nextConfig,
        ])
      }
    } catch (saveError) {
      setError(
        saveError?.response?.data?.message ||
        saveError?.message ||
        'บันทึกการตั้งค่าไม่สำเร็จ',
      )
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <DebugFieldVisibilityContext.Provider
      value={{ isDebugMode, isConfigReady, isVisible, setVisible, save, hasChanges, isSaving, error }}
    >
      {isDebugMode && showControls && (
        <div className="mb-3">
          {error && <Alert variant="danger">{error}</Alert>}
          <Button variant="outline-primary" disabled={!hasChanges || isSaving} onClick={save}>
            {isSaving ? 'กำลังบันทึก...' : 'บันทึกการแสดงผล'}
          </Button>
        </div>
      )}
      {children}
    </DebugFieldVisibilityContext.Provider>
  )
}

export const useDebugFieldVisibility = () => useContext(DebugFieldVisibilityContext)

export const DebugFieldVisibility = ({ fieldKey, label, children }) => {
  const visibility = useContext(DebugFieldVisibilityContext)
  if (!visibility) return children
  const visible = visibility.isVisible(fieldKey)
  if (!visibility.isDebugMode && !visible) return null

  return (
    <div style={visibility.isDebugMode && !visible ? { opacity: 0.45 } : undefined}>
      {visibility.isDebugMode && (
        <Form.Check
          type="checkbox"
          checked={visible}
          label={`แสดง ${label}`}
          aria-label={`แสดง ${label}`}
          onChange={(event) => visibility.setVisible(fieldKey, event.target.checked)}
        />
      )}
      {children}
    </div>
  )
}
